import React, { useState, useEffect } from 'react';
import { buildApiUrl, getServerUrl } from '../config';
import jsPDF from 'jspdf';

// Display a date string or a term label (e.g. "Term 1") gracefully
const fmtDate = (dateStr, locale = 'en-GB', options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) => {
  if (!dateStr) return '';
  if (/^Term\s*\d+$/i.test(String(dateStr).trim())) return dateStr;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(locale, options);
};

const TERM_OPTIONS = ['Term 1', 'Term 2', 'Term 3'];

const ParentsEveningSystem = ({ userRole, userEmail, userName, students = [], onBack }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Set default active tab based on user role
  const [activeTab, setActiveTab] = useState(
    userRole === 'admin' ? 'manage' : 'events'
  );

  useEffect(() => {
    loadEvents();
  }, [userRole, userEmail]);

  // Auto-refresh events every 30 seconds to catch new/updated events
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      loadEvents();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, [userRole, userEmail]);

  const loadEvents = async () => {
    try {
      let url = `/api/parents-evening/events?user_role=${userRole}`;
      // Only pass parent_email if user is actually a parent
      if (userEmail && userRole === 'parent') {
        url += `&parent_email=${encodeURIComponent(userEmail)}`;
      }
      console.log(`[PARENTS_EVENING] 🔄 Fetching events from: ${url}`);
      const response = await fetch(buildApiUrl(url));
      const data = await response.json();
      console.log(`[PARENTS_EVENING] 📡 API Response:`, data);
      if (data.success) {
        console.log(`[PARENTS_EVENING] ✅ Success! Received ${data.events.length} events`);
        data.events.forEach((evt, idx) => {
          console.log(`[PARENTS_EVENING]   ${idx + 1}. ${evt.name} (published=${evt.published}, active=${evt.active}, status=${evt.status})`);
        });
        setEvents(data.events);
        if (data.events.length > 0) {
          setSelectedEvent(prev => {
            if (!prev) return data.events[0];
            // Keep current selection if it still exists; otherwise fall back to first
            const stillExists = data.events.find(e => e.id === prev.id);
            return stillExists || prev;
          });
        } else {
          setSelectedEvent(null);
        }
      } else {
        console.error(`[PARENTS_EVENING] ❌ API returned success=false:`, data);
      }
    } catch (error) {
      console.error(`[PARENTS_EVENING] ❌ Error loading events:`, error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with Back Button */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Home"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <h1 className="text-3xl">📋</h1>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Event Booking</h2>
            <p className="text-sm text-gray-500">Parents Evening · Wrap Around Care · Other Events</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs - Only show for non-admin or when admin wants to switch views */}
      {(userRole !== 'admin' || activeTab !== 'manage') && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {userRole === 'admin' && (
              <button
                onClick={() => setActiveTab('manage')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'manage'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ⚙️ Admin Panel
              </button>
            )}
            
            <button
              onClick={() => setActiveTab('events')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'events'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              � Events
            </button>
            
            {userRole === 'parent' && (
              <button
                onClick={() => setActiveTab('booking')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'booking'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                � Book Appointments
              </button>
            )}
            
            {userRole === 'teacher' && (
              <button
                onClick={() => setActiveTab('schedule')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'schedule'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📅 My Schedule
              </button>
            )}
          </nav>
        </div>
      )}

      {/* Content */}
  <div className={userRole === 'admin' && activeTab === 'manage' ? '' : 'bg-white rounded-lg shadow overflow-visible'}>
        {activeTab === 'events' && (
          <EventsTab 
            events={events} 
            selectedEvent={selectedEvent} 
            setSelectedEvent={setSelectedEvent}
            userRole={userRole}
            setActiveTab={setActiveTab}
            refreshing={refreshing}
            setRefreshing={setRefreshing}
            loadEvents={loadEvents}
          />
        )}
        
        {activeTab === 'booking' && userRole === 'parent' && (
          <BookingTab 
            selectedEvent={selectedEvent} 
            parentEmail={userEmail} 
            parentName={userName}
            students={students}
          />
        )}
        
        {activeTab === 'schedule' && userRole === 'teacher' && (
          <TeacherScheduleTab 
            selectedEvent={selectedEvent} 
            teacherId={userEmail}
            userName={userName}
          />
        )}
        
        {activeTab === 'manage' && userRole === 'admin' && (
          <ManageTab userRole={userRole} userEmail={userEmail} onEventCreated={loadEvents} />
        )}
      </div>
    </div>
  );
};

// Events Tab Component
const EventsTab = ({ events, selectedEvent, setSelectedEvent, userRole, setActiveTab, refreshing, setRefreshing, loadEvents }) => {
  const handleEventClick = (event) => {
    console.log(`📅 Event clicked: ${event.name} by ${userRole}`);
    setSelectedEvent(event);
    
    // Auto-navigate to appropriate tab based on user role
    if (userRole === 'parent') {
      console.log(`👨‍👩‍👧‍👦 Parent clicked event, navigating to booking tab`);
      setActiveTab('booking');
    } else if (userRole === 'teacher') {
      console.log(`👨‍🏫 Teacher clicked event, navigating to schedule tab`);
      setActiveTab('schedule');
    }
  };

  return (
    <div className="p-6 w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Available Events</h2>
        <button
          onClick={() => {
            setRefreshing(true);
            loadEvents();
          }}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          title="Refresh events list"
        >
          {refreshing ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              <span>Refreshing...</span>
            </>
          ) : (
            <>
              <span>🔄</span>
              <span>Refresh</span>
            </>
          )}
        </button>
      </div>
      
      {events.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📅</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Events Available</h3>
          <p className="text-gray-600">No events are currently scheduled.</p>
        </div>
      ) : (
        <>
          {userRole === 'parent' && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                💡 <strong>Click on an event below</strong> to view available appointment slots and book meetings with teachers.
              </p>
            </div>
          )}
          {userRole === 'teacher' && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                📅 <strong>Click on an event below</strong> to view your schedule and upcoming appointments.
              </p>
            </div>
          )}
          
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-max w-full">
          {events.map((event) => (
            <div
              key={event.id}
              className={`border rounded-lg cursor-pointer transition-colors flex flex-col justify-between overflow-hidden hover:shadow-md w-full min-h-0 ${
                selectedEvent?.id === event.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleEventClick(event)}
            >
              <div className="p-4 flex-1 flex flex-col justify-between">
                {/* Event type badge */}
                <div className="mb-2">
                  {event.event_type === 'wrap_around_care' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">🌅 Wrap Around Care</span>
                  ) : event.event_type === 'others' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">📌 Other Event</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">🎓 Parents Evening</span>
                  )}
                </div>
                <h3 className="font-medium text-gray-900 mb-2 text-base overflow-hidden leading-tight" 
                    style={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.3em',
                      maxHeight: '2.6em'
                    }}>
                  {event.name}
                </h3>
                <div className="text-sm text-gray-600 space-y-1 flex-1">
                  <div className="flex items-center">
                    <span className="mr-2">📅</span>
                    <span className="truncate">
                      {fmtDate(event.date, 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">🕐</span>
                    <span className="truncate">{event.start_time} - {event.end_time}</span>
                  </div>
                  {event.event_type === 'wrap_around_care' ? (
                    <>
                      {event.morning_cost && (
                        <div className="flex items-center">
                          <span className="mr-2">🌄</span>
                          <span className="truncate">Morning: QAR {event.morning_cost}</span>
                        </div>
                      )}
                      {event.afternoon_cost && (
                        <div className="flex items-center">
                          <span className="mr-2">🌇</span>
                          <span className="truncate">Afternoon: £{event.afternoon_cost}</span>
                        </div>
                      )}
                    </>
                  ) : event.event_type === 'others' ? null : (
                    <>
                      <div className="flex items-center">
                        <span className="mr-2">💻</span>
                        <span className="truncate">{event.meeting_type === 'online' ? 'Online' : 'In-Person'}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">⏱️</span>
                        <span className="truncate">{event.slot_duration} min slots</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                    (event.active !== false && event.status !== 'closed') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {(event.active !== false && event.status !== 'closed') ? 'ACTIVE' : 'CLOSED'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
};

// Booking Tab Component - dispatches to the correct booking UI based on event type
const BookingTab = ({ selectedEvent, parentEmail, parentName, students = [] }) => {
  if (!selectedEvent) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-400 text-6xl mb-4">📅</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Event Selected</h3>
        <p className="text-gray-600">Please select an event from the Events tab.</p>
      </div>
    );
  }
  if (selectedEvent.event_type === 'wrap_around_care') {
    return <WrapAroundCareRegistration selectedEvent={selectedEvent} parentEmail={parentEmail} parentName={parentName} students={students} />;
  }
  if (selectedEvent.event_type === 'others') {
    return <OthersRegistration selectedEvent={selectedEvent} parentEmail={parentEmail} parentName={parentName} />;
  }
  // Default: parents evening slot booking
  return <ParentsEveningBookingContent selectedEvent={selectedEvent} parentEmail={parentEmail} parentName={parentName} />;
};

// -------- Wrap Around Care Registration --------
const WrapAroundCareRegistration = ({ selectedEvent, parentEmail, parentName, students = [] }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionChoice, setSessionChoice] = useState('morning');
  const [selectedChild, setSelectedChild] = useState(() => students.length > 0 ? students[0] : null);

  useEffect(() => {
    if (selectedEvent && parentEmail) loadRegistrations();
  }, [selectedEvent, parentEmail]);

  const loadRegistrations = async () => {
    try {
      const res = await fetch(buildApiUrl(`/api/event-booking/registrations/${selectedEvent.id}?parent_email=${encodeURIComponent(parentEmail)}`));
      const data = await res.json();
      if (data.success) setRegistrations(data.registrations);
    } catch (e) { console.error(e); }
  };

  const handleRegister = async () => {
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/event-booking/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: selectedEvent.id,
          parent_email: parentEmail,
          parent_name: parentName,
          student_name: selectedChild ? selectedChild.full_name : parentName,
          session_type: sessionChoice,
        }),
      });
      const data = await res.json();
      if (data.success) {
        alert('Registration confirmed!');
        loadRegistrations();
      } else {
        alert('Registration failed: ' + (data.detail || data.error || 'Unknown error'));
      }
    } catch (e) { alert('Registration failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleCancel = async (regId) => {
    if (!window.confirm('Cancel this registration?')) return;
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/api/event-booking/registrations/${regId}`), { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { alert('Registration cancelled.'); loadRegistrations(); }
      else alert(data.error || 'Failed to cancel.');
    } catch (e) { alert('Failed to cancel.'); }
    finally { setLoading(false); }
  };

  const morningCost = selectedEvent.morning_cost;
  const afternoonCost = selectedEvent.afternoon_cost;
  const bothCost = (morningCost && afternoonCost) ? (parseFloat(morningCost) + parseFloat(afternoonCost)).toFixed(2) : null;

  // Only look at registrations for the currently selected child
  const currentChildName = selectedChild ? selectedChild.full_name : parentName;
  const childRegistrations = registrations.filter(r => r.student_name === currentChildName);

  const alreadyRegistered = (session) => childRegistrations.some(r => r.session_type === session || r.session_type === 'both');

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-4">🌅 Wrap Around Care Registration</h2>

      {/* Child photo-card selector */}
      {students.length > 1 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Select Child</label>
          <div className="flex flex-wrap gap-3">
            {students.map((student) => {
              const isSelected = selectedChild?.school_id === student.school_id;
              return (
                <button
                  key={student.school_id}
                  onClick={() => setSelectedChild(student)}
                  className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                    isSelected ? 'border-orange-500 bg-orange-50 shadow-md' : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-gray-50'
                  }`}
                >
                  <img
                    src={`${getServerUrl()}/student-photo/${student.school_id}?v=3`}
                    alt={student.full_name}
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student.full_name)}&background=ffedd5&color=c2410c&size=64`; }}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                  />
                  <span className={`mt-2 text-xs font-medium text-center max-w-[80px] truncate ${isSelected ? 'text-orange-700' : 'text-gray-700'}`}>
                    {student.full_name.split(' ')[0]}
                  </span>
                  {student.form && (
                    <span className="text-xs text-gray-400">{student.form}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <p className="font-medium text-orange-900">{selectedEvent.name}</p>
        <p className="text-sm text-orange-700">📅 {fmtDate(selectedEvent.date)}</p>
        <p className="text-sm text-orange-700">🕐 {selectedEvent.start_time} – {selectedEvent.end_time}</p>
      </div>

      {/* Session costs info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {morningCost && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="text-2xl mb-1">🌄</div>
            <p className="font-semibold text-gray-800">Morning Session</p>
            {(selectedEvent.morning_start || selectedEvent.morning_end) && (
              <p className="text-sm text-gray-600 mb-1">🕐 {selectedEvent.morning_start || '—'} – {selectedEvent.morning_end || '—'}</p>
            )}
            <p className="text-2xl font-bold text-yellow-700">QAR {morningCost}</p>
          </div>
        )}
        {afternoonCost && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-2xl mb-1">🌇</div>
            <p className="font-semibold text-gray-800">Afternoon Session</p>
            {(selectedEvent.afternoon_start || selectedEvent.afternoon_end) && (
              <p className="text-sm text-gray-600 mb-1">🕐 {selectedEvent.afternoon_start || '—'} – {selectedEvent.afternoon_end || '—'}</p>
            )}
            <p className="text-2xl font-bold text-blue-700">QAR {afternoonCost}</p>
          </div>
        )}
      </div>

      {/* Current registrations for selected child */}
      {childRegistrations.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Registrations for {currentChildName}</h3>
          <div className="space-y-2">
            {childRegistrations.map(reg => (
              <div key={reg.id} className="flex justify-between items-center bg-green-50 border border-green-200 rounded-lg p-3">
                <div>
                  <p className="font-medium text-green-900 capitalize">{reg.session_type.replace('_', ' ')} Session</p>
                  {reg.cost && <p className="text-sm text-green-700">Cost: QAR {reg.cost}</p>}
                </div>
                <button onClick={() => handleCancel(reg.id)} disabled={loading}
                  className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors disabled:opacity-50">
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Registration form — only shown if no booking exists yet for this child */}
      {childRegistrations.length === 0 ? (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-medium mb-4">Register for a Session</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Session</label>
            <div className="space-y-2">
              {morningCost && (
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${sessionChoice === 'morning' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="session" value="morning" checked={sessionChoice === 'morning'} onChange={() => setSessionChoice('morning')} className="mr-3" />
                  <span className="flex-1">🌄 Morning Session</span>
                  <span className="font-semibold text-yellow-700">QAR {morningCost}</span>
                </label>
              )}
              {afternoonCost && (
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${sessionChoice === 'afternoon' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="session" value="afternoon" checked={sessionChoice === 'afternoon'} onChange={() => setSessionChoice('afternoon')} className="mr-3" />
                  <span className="flex-1">🌇 Afternoon Session</span>
                  <span className="font-semibold text-blue-700">QAR {afternoonCost}</span>
                </label>
              )}
              {bothCost && (
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${sessionChoice === 'both' ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="session" value="both" checked={sessionChoice === 'both'} onChange={() => setSessionChoice('both')} className="mr-3" />
                  <span className="flex-1">🌅 Both Sessions</span>
                  <span className="font-semibold text-purple-700">QAR {bothCost}</span>
                </label>
              )}
            </div>
          </div>
          <button onClick={handleRegister} disabled={loading}
            className="w-full py-3 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? 'Registering...' : 'Confirm Registration'}
          </button>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-3xl mb-2">✅</div>
          <p className="font-semibold text-green-800">Booking complete for {currentChildName}</p>
          <p className="text-sm text-green-600 mt-1">Cancel the existing booking above if you need to change your session.</p>
        </div>
      )}
    </div>
  );
};

// -------- Others Registration --------
const OthersRegistration = ({ selectedEvent, parentEmail, parentName }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedEvent && parentEmail) loadRegistrations();
  }, [selectedEvent, parentEmail]);

  const loadRegistrations = async () => {
    try {
      const res = await fetch(buildApiUrl(`/api/event-booking/registrations/${selectedEvent.id}?parent_email=${encodeURIComponent(parentEmail)}`));
      const data = await res.json();
      if (data.success) setRegistrations(data.registrations);
    } catch (e) { console.error(e); }
  };

  const isRegistered = registrations.length > 0;

  const handleRegister = async () => {
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/event-booking/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: selectedEvent.id,
          parent_email: parentEmail,
          parent_name: parentName,
          student_name: parentName,
          session_type: 'general',
        }),
      });
      const data = await res.json();
      if (data.success) { alert('You have been registered for this event!'); loadRegistrations(); }
      else alert('Registration failed: ' + (data.detail || data.error || 'Unknown error'));
    } catch (e) { alert('Registration failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleCancel = async (regId) => {
    if (!window.confirm('Cancel your registration for this event?')) return;
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/api/event-booking/registrations/${regId}`), { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { alert('Registration cancelled.'); loadRegistrations(); }
      else alert(data.error || 'Failed to cancel.');
    } catch (e) { alert('Failed to cancel.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-4">📌 Event Registration</h2>
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="font-medium text-gray-900">{selectedEvent.name}</p>
        <p className="text-sm text-gray-700">📅 {fmtDate(selectedEvent.date)}</p>
        <p className="text-sm text-gray-700">🕐 {selectedEvent.start_time} – {selectedEvent.end_time}</p>
      </div>

      {isRegistered ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="font-medium text-green-900">✅ You are registered for this event</p>
            <p className="text-sm text-green-700">Registered on {new Date(registrations[0].registration_time).toLocaleDateString()}</p>
          </div>
          <button onClick={() => handleCancel(registrations[0].id)} disabled={loading}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50">
            Cancel Registration
          </button>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">No registration required in advance — just click below to confirm your attendance.</p>
          <button onClick={handleRegister} disabled={loading}
            className="px-8 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50">
            {loading ? 'Registering...' : '✔ Register for this Event'}
          </button>
        </div>
      )}
    </div>
  );
};

// -------- Parents Evening Booking Content (original BookingTab logic) --------
const ParentsEveningBookingContent = ({ selectedEvent, parentEmail, parentName }) => {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [deletingBooking, setDeletingBooking] = useState(null);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [needTranslator, setNeedTranslator] = useState(false);

  useEffect(() => {
    if (selectedEvent && parentEmail) {
      loadChildren();
      loadBookings();
    }
  }, [selectedEvent, parentEmail]);

  useEffect(() => {
    if (selectedChild && selectedEvent) {
      loadSlots();
    }
  }, [selectedChild, selectedEvent]);

  const loadChildren = async () => {
    try {
      console.log(`👨‍👩‍👧‍👦 Loading children for parent: ${parentEmail}`);
      let url = `/api/parents-evening/parent-teachers/${parentEmail}`;
      if (selectedEvent && selectedEvent.id) {
        url += `?event_id=${selectedEvent.id}`;
        console.log(`🎯 Filtering children by event: ${selectedEvent.id}`);
      }
      const response = await fetch(buildApiUrl(url));
      const data = await response.json();
      console.log(`📋 Parent-teachers API response:`, data);
      if (data.success) {
        setChildren(data.children);
        console.log(`✅ Found ${data.children.length} children:`, data.children);
        if (data.children.length > 0) {
          setSelectedChild(data.children[0]);
          console.log(`👦 Selected child:`, data.children[0]);
        }
      } else {
        console.error(`❌ Failed to load children:`, data);
      }
    } catch (error) {
      console.error('Error loading children:', error);
    }
  };

  const loadSlots = async () => {
    if (!selectedEvent || !selectedChild) return;
    try {
      // Use class-filtered endpoint if student has a form/class assigned
      let apiUrl;
      if (selectedChild.student_form) {
        apiUrl = `/api/parents-evening/slots/${selectedEvent.id}/${selectedChild.student_form}`;
        console.log(`🔍 Loading slots for class: ${selectedChild.student_form}, URL: ${apiUrl}`);
      } else {
        // Fallback to all slots if no class information
        apiUrl = `/api/parents-evening/slots/${selectedEvent.id}`;
        console.log(`🔍 Loading all slots, URL: ${apiUrl}`);
      }
      
      const response = await fetch(buildApiUrl(apiUrl));
      const data = await response.json();
      console.log(`📊 API Response:`, data);
      if (data.success) {
        setSlots(data.slots);
        console.log(`✅ Loaded ${data.slots.length} slots for class ${selectedChild.student_form}`);
      } else {
        console.error(`❌ Failed to load slots:`, data);
      }
    } catch (error) {
      console.error('Error loading slots:', error);
    }
  };

  const loadBookings = async () => {
    if (!selectedEvent) return;
    try {
      console.log(`🔍 Loading bookings for event: ${selectedEvent.id}, parent: ${parentEmail}`);
      const url = buildApiUrl(`/api/parents-evening/bookings/${selectedEvent.id}?parent_email=${parentEmail}`);
      console.log(`📡 Bookings API URL: ${url}`);
      
      const response = await fetch(url);
      console.log(`📊 Bookings response status: ${response.status}`);
      
      const data = await response.json();
      console.log(`📋 Bookings response data:`, data);
      
      if (data.success) {
        setBookings(data.bookings);
        console.log(`✅ Loaded ${data.bookings.length} bookings`);
      } else {
        console.error('❌ Failed to load bookings:', data.error);
      }
    } catch (error) {
      console.error('❌ Error loading bookings:', error);
    }
  };

  const bookSlot = async (slot) => {
    if (!selectedChild) return;
    
    setLoading(true);
    try {
      const bookingData = {
        event_id: selectedEvent.id,
        slot_id: slot.id,
        parent_email: parentEmail,
        parent_name: parentName,
        student_id: selectedChild.student_id,
        student_name: selectedChild.student_name,
        teacher_id: slot.teacher_id,
        teacher_name: slot.teacher_name,
        booking_time: new Date().toISOString(), // Add the missing booking_time field
        status: "confirmed",
        notes: "",
        need_translator: needTranslator ? "Yes" : "No"
      };
      
      console.log('🎯 Booking data:', bookingData);
      
      const response = await fetch(buildApiUrl('/api/parents-evening/book'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });
      
      const data = await response.json();
      console.log('📋 Booking response:', data);
      
      if (data.success) {
        const meetingDetails = data.meeting_details;
        let successMessage = 'Booking confirmed! ';
        
        if (meetingDetails?.demo_mode) {
          successMessage += 'You will receive an email with the meeting details.\n\n📧 Note: This is a demo/test booking. In production mode, real Teams meetings and calendar invitations would be created automatically.';
        } else if (meetingDetails?.meeting_id) {
          successMessage += `A real Teams meeting has been created and calendar invitations have been sent!\n\n📅 Meeting ID: ${meetingDetails.conference_id || meetingDetails.meeting_id}\n🎯 Organizer: ${meetingDetails.organizer}\n\nCheck your email and calendar for the meeting details.`;
        } else {
          successMessage += 'A Teams meeting has been created and you will receive calendar invitations shortly.';
        }
        
        alert(successMessage);
        loadSlots();
        loadBookings();
      } else {
        alert('Booking failed: ' + (data.detail || data.error || 'Unknown error'));
        console.error('❌ Booking failed:', data);
      }
    } catch (error) {
      console.error('Error booking slot:', error);
      alert('Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBooking = async (newSlotId) => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/parents-evening/bookings/${editingBooking.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: editingBooking.id,
          event_id: editingBooking.event_id,
          slot_id: newSlotId,
          parent_email: parentEmail,
          parent_name: parentName,
          student_id: editingBooking.student_id,
          student_name: editingBooking.student_name,
          teacher_id: editingBooking.teacher_id,
          teacher_name: editingBooking.teacher_name,
          booking_time: editingBooking.booking_time,
          status: 'confirmed',
          meeting_link: editingBooking.meeting_link,
          notes: editingBooking.notes || ''
        }),
      });

      const data = await response.json();
      if (data.success) {
        alert('Booking updated successfully!');
        await loadBookings();
        await loadSlots();
        setEditingBooking(null);
      } else {
        // Show specific message for edit failures
        alert('Edit is not available please cancel the booking and book again');
        setEditingBooking(null);
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      // Show specific message for edit failures
      alert('Edit is not available please cancel the booking and book again');
      setEditingBooking(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBooking = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/parents-evening/bookings/${deletingBooking.id}`), {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        alert('Booking cancelled successfully!');
        await loadBookings();
        await loadSlots();
        setDeletingBooking(null);
      } else {
        alert(data.error || 'Failed to cancel booking.');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Cancellation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedEvent) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-400 text-6xl mb-4">📅</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Event Selected</h3>
        <p className="text-gray-600">Please select an event from the Events tab to start booking appointments.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-4">Book Appointments</h2>
      
      {/* Child Selection */}
      {children.length > 1 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Select Child</label>
          <div className="flex flex-wrap gap-3">
            {children.map((child) => {
              const isSelected = selectedChild?.student_id === child.student_id;
              return (
                <button
                  key={child.student_id}
                  onClick={() => setSelectedChild(child)}
                  className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <img
                    src={`${getServerUrl()}/student-photo/${child.student_id}?v=3`}
                    alt={child.student_name}
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(child.student_name)}&background=dbeafe&color=1d4ed8&size=64`; }}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                  />
                  <span className={`mt-2 text-xs font-medium text-center max-w-[80px] truncate ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                    {child.student_name.split(' ')[0]}
                  </span>
                  {child.student_form && (
                    <span className="text-xs text-gray-400">{child.student_form}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* My Bookings */}
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-4">Your Confirmed Appointments</h3>
        {bookings.length > 0 ? (
          <div className="space-y-3">
            {bookings.map((booking) => (
              <div key={booking.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    <h4 className="font-medium text-green-900">{booking.teacher_name}</h4>
                    <p className="text-sm text-green-700">
                      Student: {booking.student_name}
                    </p>
                    <p className="text-sm text-green-700">
                      Time: {booking.start_time} - {booking.end_time}
                    </p>
                    {booking.meeting_link && (
                      <div className="mt-2">
                        <a
                          href={booking.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          🔗 Join Teams Meeting
                        </a>
                        {booking.demo_mode && (
                          <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                            Demo Mode
                          </span>
                        )}
                        {booking.meeting_id && (
                          <p className="text-xs text-gray-500 mt-1">
                            Meeting ID: {booking.conference_id || booking.meeting_id}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col space-y-2 ml-4">
                    <button
                      onClick={() => setEditingBooking(booking)}
                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingBooking(booking)}
                      className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-gray-400 text-4xl mb-2">📅</div>
            <p className="text-gray-600 font-medium">No appointments booked yet</p>
            <p className="text-gray-500 text-sm mt-1">Your confirmed appointments will appear here</p>
          </div>
        )}
      </div>

      {/* Available Slots */}
      {selectedChild && (
        <div>
          {/* Class information message */}
          {selectedChild.student_form && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                📚 Showing available slots for <strong>{selectedChild.student_name}</strong>'s class teachers (<strong>{selectedChild.student_form}</strong>).
                Only teachers assigned to this class are displayed.
              </p>
            </div>
          )}
          
          <h3 className="text-lg font-medium mb-4">Available Teachers & Time Slots</h3>
          
          {/* Group slots by teacher */}
          {Object.entries(
            slots
              .filter(slot => slot.status === 'available')
              .reduce((acc, slot) => {
                if (!acc[slot.teacher_id]) {
                  acc[slot.teacher_id] = {
                    teacher_name: slot.teacher_name,
                    slots: []
                  };
                }
                acc[slot.teacher_id].slots.push(slot);
                return acc;
              }, {})
          ).map(([teacherId, teacherData]) => (
            <div key={teacherId} className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">{teacherData.teacher_name}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {teacherData.slots.map((slot) => {
                  const isBooked = bookings.some(b => 
                    b.teacher_id === teacherId && 
                    b.student_id === selectedChild.student_id && 
                    b.status === 'confirmed'
                  );
                  
                  return (
                    <button
                      key={slot.id}
                      onClick={() => !isBooked && setBookingSlot(slot)}
                      disabled={loading || isBooked}
                      className={`p-2 text-sm rounded border transition-colors ${
                        isBooked
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <div className="whitespace-nowrap">{slot.start_time}</div>
                      <div className="text-xs whitespace-nowrap">{slot.end_time}</div>
                      {isBooked && <div className="text-xs text-gray-500 mt-1">Booked</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Booking Modal */}
      {editingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">Edit Appointment</h3>
              <p className="text-sm text-gray-600 mb-4">
                Current: {editingBooking.teacher_name} - {editingBooking.start_time} to {editingBooking.end_time}
              </p>
              <p className="text-sm font-medium mb-4">Select a new time slot:</p>
              
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {slots
                  .filter(slot => 
                    slot.status === 'available' && 
                    slot.teacher_id === editingBooking.teacher_id
                  )
                  .map((slot) => (
                    <div key={slot.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">
                            {slot.start_time} - {slot.end_time}
                          </p>
                          <p className="text-xs text-gray-500">
                            {slot.teacher_name}
                          </p>
                        </div>
                        <button
                          onClick={() => handleEditBooking(slot.id)}
                          className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              
              {slots.filter(slot => 
                slot.status === 'available' && 
                slot.teacher_id === editingBooking.teacher_id
              ).length === 0 && (
                <p className="text-gray-500 text-center py-4">
                  No other available slots for this teacher
                </p>
              )}
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditingBooking(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Translator Confirmation Modal */}
      {bookingSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">Confirm Booking</h3>
              <p className="text-sm text-gray-600 mb-4">
                Booking appointment with <strong>{bookingSlot.teacher_name}</strong>
              </p>
              <p className="text-sm text-gray-600 mb-6">
                Time: <strong>{bookingSlot.start_time} - {bookingSlot.end_time}</strong>
              </p>
              
              <div className="mb-6">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={needTranslator}
                    onChange={(e) => setNeedTranslator(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    🌐 I need a translator for this meeting
                  </span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setBookingSlot(null);
                    setNeedTranslator(false);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    bookSlot(bookingSlot);
                    setBookingSlot(null);
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Confirm Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">Cancel Appointment</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to cancel your appointment with{' '}
                <strong>{deletingBooking.teacher_name}</strong> on{' '}
                <strong>{deletingBooking.start_time} - {deletingBooking.end_time}</strong>?
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingBooking(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  Keep Appointment
                </button>
                <button
                  onClick={handleDeleteBooking}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  Cancel Appointment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Teacher Schedule Tab Component
const TeacherScheduleTab = ({ selectedEvent, teacherId, userName }) => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockingSlot, setBlockingSlot] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (selectedEvent && teacherId) {
      setError(null);
      loadSchedule();
    }
  }, [selectedEvent, teacherId]);

  // Auto-refresh schedule every 30 seconds to catch booking cancellations
  useEffect(() => {
    if (!selectedEvent || !teacherId) return;
    
    const refreshInterval = setInterval(() => {
      loadSchedule();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, [selectedEvent, teacherId]);

  const loadSchedule = async () => {
    try {
      setError(null);
      console.log(`🔍 Loading schedule for teacher: ${teacherId}, event: ${selectedEvent.id}`);
      const url = buildApiUrl(`/api/parents-evening/teacher-schedule/${teacherId}/${selectedEvent.id}`);
      console.log(`📡 API URL: ${url}`);
      
      const response = await fetch(url);
      console.log(`📊 Response status: ${response.status}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`📋 Response data:`, data);
      
      if (data.success) {
        setSchedule(data.schedule);
        console.log(`✅ Loaded ${data.schedule.length} schedule items`);
      } else {
        console.error('❌ API returned success: false', data);
        setError(data.error || 'Failed to load schedule');
        setSchedule([]);
      }
    } catch (error) {
      console.error('❌ Error loading schedule:', error);
      setError(`Connection error: ${error.message}`);
      setSchedule([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotToggle = async (slotId, action) => {
    try {
      setBlockingSlot(slotId);
      const response = await fetch(buildApiUrl(`/api/parents-evening/teacher-block-slot/${slotId}?action=${action}&teacher_id=${teacherId}`), {
        method: 'PUT',
      });
      
      const data = await response.json();
      if (data.success) {
        // Reload schedule to reflect changes
        await loadSchedule();
      } else {
        alert(data.error || 'Failed to update slot');
      }
    } catch (error) {
      console.error('Error updating slot:', error);
      alert('Failed to update slot. Please try again.');
    } finally {
      setBlockingSlot(null);
    }
  };

  if (!selectedEvent) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-400 text-6xl mb-4">📅</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Event Selected</h3>
        <p className="text-gray-600">Please select an event from the Events tab to view your schedule.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-400 text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Schedule</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => loadSchedule()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      
      // Prepare data for Excel
      const excelData = schedule.map(slot => ({
        'Time': `${slot.start_time} - ${slot.end_time}`,
        'Status': slot.status === 'booked' ? 'Booked' : slot.status === 'blocked' ? 'Blocked' : 'Available',
        'Parent': slot.status === 'booked' ? slot.parent_name : '-',
        'Student': slot.status === 'booked' ? slot.student_name : '-',
        'Needs Translator': slot.status === 'booked' && slot.needs_translator ? 'Yes' : 'No',
        'Notes': slot.status === 'booked' ? (slot.notes || '-') : '-'
      }));

      // Create worksheet
      const ws_data = [
        ['Parents Evening Schedule'],
        [],
        ['Event:', selectedEvent.name],
        ['Date:', fmtDate(selectedEvent.date, 'en-GB', {})],
        ['Teacher:', userName || teacherId],
        [],
        ['Time', 'Status', 'Parent', 'Student', 'Needs Translator', 'Notes'],
        ...excelData.map(row => [row.Time, row.Status, row.Parent, row.Student, row['Needs Translator'], row.Notes])
      ];

      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 15 },  // Time
        { wch: 12 },  // Status
        { wch: 25 },  // Parent
        { wch: 25 },  // Student
        { wch: 18 },  // Needs Translator
        { wch: 30 }   // Notes
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Schedule');
      
      // Generate filename with event name and date
      const fileName = `Parents_Evening_Schedule_${selectedEvent.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export to Excel. Please try again.');
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = 20;

      // Title
      doc.setFontSize(18);
      doc.text('Parents Evening Schedule', margin, yPosition);
      yPosition += 10;

      // Event details
      doc.setFontSize(12);
      doc.text(`Event: ${selectedEvent.name}`, margin, yPosition);
      yPosition += 7;
      doc.text(`Date: ${fmtDate(selectedEvent.date, 'en-GB', {})}`, margin, yPosition);
      yPosition += 7;
      doc.text(`Teacher: ${userName || teacherId}`, margin, yPosition);
      yPosition += 10;

      // Schedule slots
      doc.setFontSize(10);
      schedule.forEach((slot) => {
        // Check if we need a new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }

        // Time and Status
        doc.setFont(undefined, 'bold');
        doc.text(`${slot.start_time} - ${slot.end_time}`, margin, yPosition);
        
        const statusText = slot.status === 'booked' ? 'Booked' : slot.status === 'blocked' ? 'Blocked' : 'Available';
        doc.text(statusText, margin + 50, yPosition);
        doc.setFont(undefined, 'normal');
        
        yPosition += 5;

        // Booking details if booked
        if (slot.status === 'booked') {
          doc.text(`Parent: ${slot.parent_name || 'N/A'}`, margin + 5, yPosition);
          yPosition += 5;
          doc.text(`Student: ${slot.student_name || 'N/A'}`, margin + 5, yPosition);
          yPosition += 5;
          
          if (slot.needs_translator) {
            doc.text('Needs Translator: Yes', margin + 5, yPosition);
            yPosition += 5;
          }
          
          if (slot.notes) {
            doc.text(`Notes: ${slot.notes}`, margin + 5, yPosition);
            yPosition += 5;
          }
        }

        yPosition += 3; // Add space between slots
      });

      // Generate filename
      const fileName = `Parents_Evening_Schedule_${selectedEvent.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export to PDF. Please try again.');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">My Schedule - {selectedEvent.name}</h2>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setRefreshing(true);
              loadSchedule().finally(() => setRefreshing(false));
            }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <span>🔄</span>
                <span>Refresh Schedule</span>
              </>
            )}
          </button>
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            📊 Export to Excel
          </button>
          <button
            onClick={exportToPDF}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            📄 Export to PDF
          </button>
        </div>
      </div>
      
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 You can block time slots to mark them as unavailable for parents to book (e.g., for breaks or other commitments).
          Blocked slots will not be visible to parents when they try to book appointments.
        </p>
      </div>

      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-800">
          ✅ Schedule automatically refreshes every 30 seconds. When a parent cancels a booking, their slot will become available again.
          You can also click the "Refresh Schedule" button above to update immediately.
        </p>
      </div>
      
      <div className="space-y-2">
        {schedule.map((slot) => (
          <div
            key={slot.slot_id}
            className={`p-4 rounded-lg border ${
              slot.status === 'booked'
                ? 'bg-blue-50 border-blue-200'
                : slot.status === 'blocked'
                ? 'bg-red-50 border-red-200'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-4">
                  <span className="font-medium">
                    {slot.start_time} - {slot.end_time}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    slot.status === 'booked' 
                      ? 'bg-blue-100 text-blue-800' 
                      : slot.status === 'blocked'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {slot.status === 'booked' 
                      ? '📋 Booked' 
                      : slot.status === 'blocked'
                      ? '🚫 Blocked'
                      : '🕐 Available'}
                  </span>
                </div>
                
                {slot.status === 'booked' && (
                  <div className="mt-2 text-sm text-gray-700">
                    <p><strong>Parent:</strong> {slot.parent_name}</p>
                    <p><strong>Student:</strong> {slot.student_name}</p>
                    {slot.meeting_link && (
                      <a
                        href={slot.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 mt-1"
                      >
                        🔗 Join Meeting
                      </a>
                    )}
                  </div>
                )}
              </div>
              
              {/* Block/Unblock button - only show for available and blocked slots */}
              {slot.status !== 'booked' && (
                <div className="ml-4">
                  <button
                    onClick={() => handleSlotToggle(
                      slot.slot_id, 
                      slot.status === 'blocked' ? 'unblock' : 'block'
                    )}
                    disabled={blockingSlot === slot.slot_id}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      slot.status === 'blocked'
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-red-500 text-white hover:bg-red-600'
                    } ${blockingSlot === slot.slot_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {blockingSlot === slot.slot_id ? (
                      'Updating...'
                    ) : slot.status === 'blocked' ? (
                      '✅ Make Available'
                    ) : (
                      '🚫 Block Slot'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {schedule.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📅</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedule Available</h3>
          <p className="text-gray-600">No schedule has been generated for this event yet.</p>
        </div>
      )}
    </div>
  );
};

const ManageTab = ({ userRole, userEmail, onEventCreated }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventForBookings, setSelectedEventForBookings] = useState(null);
  const [bookingsData, setBookingsData] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    start_time: '16:00',
    end_time: '19:00',
    slot_duration: 10,
    meeting_type: 'online',
    years: [],
    event_type: 'parents_evening',
    morning_cost: '',
    afternoon_cost: '',
    morning_start: '',
    morning_end: '',
    afternoon_start: '',
    afternoon_end: '',
  });
  const [loading, setLoading] = useState(false);
  // State management for ManageTab (admin event management)
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      let url = `/api/parents-evening/events?user_role=${userRole}`;
      if (userEmail) {
        url += `&parent_email=${encodeURIComponent(userEmail)}`;
      }
      const response = await fetch(buildApiUrl(url));
      const data = await response.json();
      if (data.success) {
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(buildApiUrl('/api/parents-evening/events'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      if (data.success) {
        alert('🎉 Event created successfully!');
        setShowCreateForm(false);
        setFormData({
          name: '',
          date: '',
          start_time: '16:00',
          end_time: '19:00',
          slot_duration: 10,
          meeting_type: 'online',
          years: [],
          event_type: 'parents_evening',
          morning_cost: '',
          afternoon_cost: '',
          morning_start: '',
          morning_end: '',
          afternoon_start: '',
          afternoon_end: '',
        });
        loadEvents();
        if (onEventCreated) onEventCreated();
      } else {
        alert('Failed to create event: ' + data.detail);
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event? This will also delete all associated bookings and slots.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/parents-evening/events/${eventId}`), {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        alert('Event deleted successfully!');
        await loadEvents();
      } else {
        alert(data.error || 'Failed to delete event.');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Delete failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleEventStatus = async (event) => {
    const currentlyActive = event.active !== false && event.status !== 'closed';
    const newActiveStatus = !currentlyActive;
    const action = newActiveStatus ? 'activate' : 'close';
    
    if (!confirm(`Are you sure you want to ${action} this event?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/parents-evening/events/${event.id}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          active: newActiveStatus
        }),
      });

      const data = await response.json();
      if (data.success) {
        alert(`Event ${action}d successfully!`);
        await loadEvents();
      } else {
        alert(data.error || `Failed to ${action} event.`);
      }
    } catch (error) {
      console.error(`Error ${action}ing event:`, error);
      alert(`${action.charAt(0).toUpperCase() + action.slice(1)} failed. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const togglePublishStatus = async (event) => {
    const action = event.published ? 'Stop Sharing' : 'Publish to Parents';
    
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/parents-evening/events/${event.id}/toggle-publish`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        alert(`${data.message} Parents will ${data.published ? 'now' : 'no longer'} be able to see this event.`);
        await loadEvents();
      } else {
        alert(data.error || `Failed to ${action} event.`);
      }
    } catch (error) {
      console.error(`Error ${action}ing event:`, error);
      alert(`${action} failed. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const updateEvent = async (updatedEvent) => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/parents-evening/events/${updatedEvent.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedEvent),
      });

      const data = await response.json();
      if (data.success) {
        alert('Event updated successfully!');
        await loadEvents();
        setEditingEvent(null);
        setShowEditForm(false);
      } else {
        alert(data.error || 'Failed to update event.');
      }
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Update failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const viewEventBookings = async (event) => {
    try {
      setLoadingBookings(true);
      setSelectedEventForBookings(event);
      
      const eventType = event.event_type || 'parents_evening';

      if (eventType !== 'parents_evening') {
        // WAC / Others: load registrations
        const regResponse = await fetch(buildApiUrl(`/api/event-booking/registrations/${event.id}`));
        const regResult = await regResponse.json();
        setBookingsData(regResult.registrations || []);
        setScheduleData([]);
      } else {
        // Parents Evening: load teacher slots + bookings
        const bookingsResponse = await fetch(buildApiUrl(`/api/parents-evening/bookings/${event.id}`));
        const bookingsResult = await bookingsResponse.json();
        setBookingsData(bookingsResult.success ? (bookingsResult.bookings || []) : []);

        const slotsResponse = await fetch(buildApiUrl(`/api/parents-evening/slots/${event.id}`));
        const slotsResult = await slotsResponse.json();
        setScheduleData(slotsResult.success ? (slotsResult.slots || []) : []);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      alert('Failed to load bookings. Please try again.');
    } finally {
      setLoadingBookings(false);
    }
  };

  const downloadBookingsAsExcel = async () => {
    try {
      if (!selectedEventForBookings) return;
      
      const XLSX = await import('xlsx');
      
      // Get unique teachers and time slots
      const teacherMap = new Map();
      const timeSlots = new Set();
      
      // Build teacher-slot mapping
      scheduleData.forEach(slot => {
        if (!teacherMap.has(slot.teacher_id)) {
          teacherMap.set(slot.teacher_id, {
            teacher_id: slot.teacher_id,
            teacher_name: slot.teacher_name,
            slots: {}
          });
        }
        
        const slotKey = `${slot.start_time} - ${slot.end_time}`;
        timeSlots.add(slotKey);
        
        // Find if this slot is booked
        const booking = bookingsData.find(b => b.slot_id === slot.id);
        teacherMap.get(slot.teacher_id).slots[slotKey] = booking 
          ? `${booking.parent_name} (${booking.student_name})` 
          : 'Free';
      });
      
      // Sort time slots
      const sortedTimeSlots = Array.from(timeSlots).sort();
      
      // Build Excel data
      const ws_data = [
        ['Parents Evening Bookings Report'],
        [],
        ['Event:', selectedEventForBookings.name],
        ['Date:', fmtDate(selectedEventForBookings.date, 'en-GB', {})],
        ['Total Slots:', scheduleData.length],
        ['Booked:', bookingsData.length],
        ['Free:', scheduleData.length - bookingsData.length],
        [],
        ['Teacher Email', 'Teacher Name', ...sortedTimeSlots]
      ];
      
      // Add teacher rows
      for (const [teacherEmail, teacherData] of teacherMap) {
        const row = [teacherEmail, teacherData.teacher_name];
        sortedTimeSlots.forEach(slot => {
          const status = teacherData.slots[slot] || 'Free';
          row.push(status);
        });
        ws_data.push(row);
      }
      
      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      
      // Set column widths
      const colWidths = [
        { wch: 30 },  // Teacher Email
        { wch: 25 },  // Teacher Name
        ...sortedTimeSlots.map(() => ({ wch: 30 }))  // Time slots
      ];
      ws['!cols'] = colWidths;
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bookings');
      
      const fileName = `Parents_Evening_Bookings_${selectedEventForBookings.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      alert('✅ Bookings exported to Excel successfully!');
    } catch (error) {
      console.error('Error downloading bookings:', error);
      alert('Failed to export bookings. Please try again.');
    }
  };

  const downloadBookingsAsPDF = () => {
    try {
      if (!selectedEventForBookings) return;
      
      const doc = new jsPDF('landscape');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = 20;
      
      // Title
      doc.setFontSize(18);
      doc.text('Parents Evening Bookings Report', margin, yPosition);
      yPosition += 10;
      
      // Event details
      doc.setFontSize(12);
      doc.text(`Event: ${selectedEventForBookings.name}`, margin, yPosition);
      yPosition += 7;
      doc.text(`Date: ${new Date(selectedEventForBookings.date).toLocaleDateString()}`, margin, yPosition);
      yPosition += 7;
      doc.text(`Total Slots: ${scheduleData.length} | Booked: ${bookingsData.length} | Free: ${scheduleData.length - bookingsData.length}`, margin, yPosition);
      yPosition += 12;
      
      // Get unique teachers
      const teacherMap = new Map();
      scheduleData.forEach(slot => {
        if (!teacherMap.has(slot.teacher_id)) {
          teacherMap.set(slot.teacher_id, {
            teacher_name: slot.teacher_name,
            bookings: []
          });
        }
      });
      
      // Add bookings to teachers
      bookingsData.forEach(booking => {
        const slot = scheduleData.find(s => s.id === booking.slot_id);
        if (slot && teacherMap.has(slot.teacher_id)) {
          teacherMap.get(slot.teacher_id).bookings.push({
            time: `${slot.start_time} - ${slot.end_time}`,
            parent: booking.parent_name,
            student: booking.student_name,
            translator: booking.needs_translator
          });
        }
      });
      
      // Print teachers and their bookings
      doc.setFontSize(10);
      for (const [teacherId, teacherData] of teacherMap) {
        // Check if we need a new page
        if (yPosition > 180) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFont(undefined, 'bold');
        doc.text(`Teacher: ${teacherData.teacher_name}`, margin, yPosition);
        doc.setFont(undefined, 'normal');
        yPosition += 6;
        
        if (teacherData.bookings.length === 0) {
          doc.text('  No bookings', margin + 5, yPosition);
          yPosition += 6;
        } else {
          teacherData.bookings.forEach(booking => {
            if (yPosition > 180) {
              doc.addPage();
              yPosition = 20;
            }
            
            doc.text(`  ${booking.time}: ${booking.parent} (${booking.student})${booking.translator ? ' [Translator Needed]' : ''}`, margin + 5, yPosition);
            yPosition += 5;
          });
        }
        
        yPosition += 3;
      }
      
      const fileName = `Parents_Evening_Bookings_${selectedEventForBookings.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      alert('✅ Bookings exported to PDF successfully!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export to PDF. Please try again.');
    }
  };

  if (userRole !== 'admin') {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-400 text-6xl mb-4">🔒</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600">You don't have permission to manage event booking.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Bookings Modal */}
      {selectedEventForBookings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-screen overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-2xl font-semibold">📊 Bookings - {selectedEventForBookings.name}</h2>
              <button
                onClick={() => {
                  setSelectedEventForBookings(null);
                  setBookingsData([]);
                  setScheduleData([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              {loadingBookings ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (selectedEventForBookings?.event_type || 'parents_evening') !== 'parents_evening' ? (
                /* WAC / Others: registrations table */
                <>
                  <div className="mb-4 flex gap-4 items-center flex-wrap">
                    <div className="text-sm text-gray-600">
                      Total Registrations: <strong>{bookingsData.length}</strong>
                    </div>
                  </div>
                  {bookingsData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No registrations yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-100 border-b-2 border-gray-300">
                            <th className="text-left px-4 py-2 font-semibold">Parent Name</th>
                            <th className="text-left px-4 py-2 font-semibold">Email</th>
                            <th className="text-left px-4 py-2 font-semibold">Student</th>
                            {(selectedEventForBookings?.event_type) === 'wrap_around_care' && (
                              <>
                                <th className="text-left px-4 py-2 font-semibold">Session</th>
                                <th className="text-left px-4 py-2 font-semibold">Cost</th>
                              </>
                            )}
                            <th className="text-left px-4 py-2 font-semibold">Registered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bookingsData.map((reg) => (
                            <tr key={reg.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{reg.parent_name}</td>
                              <td className="px-4 py-2 text-gray-600">{reg.parent_email}</td>
                              <td className="px-4 py-2">{reg.student_name}</td>
                              {(selectedEventForBookings?.event_type) === 'wrap_around_care' && (
                                <>
                                  <td className="px-4 py-2 capitalize">{reg.session_type}</td>
                                  <td className="px-4 py-2">QAR {reg.cost || '0.00'}</td>
                                </>
                              )}
                              <td className="px-4 py-2 text-gray-500 text-xs">
                                {reg.registration_time ? new Date(reg.registration_time).toLocaleString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                /* Parents Evening: teacher schedule table */
                <>
                  <div className="mb-4 flex gap-4 items-center flex-wrap">
                    <button
                      onClick={downloadBookingsAsExcel}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      📊 Export to Excel
                    </button>
                    <button
                      onClick={downloadBookingsAsPDF}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      📄 Export to PDF
                    </button>
                    <div className="text-sm text-gray-600 flex items-center">
                      <span>Total Slots: {scheduleData.length} | Booked: {bookingsData.length} | Free: {scheduleData.length - bookingsData.length}</span>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-gray-300">
                          <th className="text-left px-4 py-2 font-semibold">Teacher</th>
                          <th className="text-left px-4 py-2 font-semibold">Email</th>
                          <th className="text-left px-4 py-2 font-semibold">Total Slots</th>
                          <th className="text-left px-4 py-2 font-semibold">Booked</th>
                          <th className="text-left px-4 py-2 font-semibold">Available</th>
                          <th className="text-left px-4 py-2 font-semibold">Need Translator</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(new Map(
                          scheduleData.map(slot => [slot.teacher_id, slot])
                        ).entries()).map(([teacherId, slot]) => {
                          const teacherSlots = scheduleData.filter(s => s.teacher_id === teacherId);
                          const bookedSlots = bookingsData.filter(b => 
                            b.teacher_id === teacherId && 
                            teacherSlots.some(s => s.id === b.slot_id)
                          );
                          const needTranslatorCount = bookedSlots.filter(b => b.need_translator === "Yes").length;
                          
                          return (
                            <tr key={teacherId} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{slot.teacher_name}</td>
                              <td className="px-4 py-2 text-gray-600">{teacherId}</td>
                              <td className="px-4 py-2">{teacherSlots.length}</td>
                              <td className="px-4 py-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {bookedSlots.length}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {teacherSlots.length - bookedSlots.length}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {needTranslatorCount} / {bookedSlots.length}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center mb-6 flex-col md:flex-row gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Event Booking Management</h2>
          <p className="text-gray-600">Create and manage parents evening, wrap around care & other events</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2 w-full md:w-auto justify-center"
        >
          <span>➕</span>
          <span>Create New Event</span>
        </button>
      </div>

      {/* Create Event Form */}
      {showCreateForm && (
        <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
          <h3 className="text-xl font-medium mb-6 text-gray-900">📅 Create New Event</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Event Type */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🗂️ Event Type *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { value: 'parents_evening', label: '🎓 Parents Evening', desc: 'Teacher appointment slots' },
                    { value: 'wrap_around_care', label: '🌅 Wrap Around Care', desc: 'Morning & afternoon sessions with cost' },
                    { value: 'others', label: '📌 Others', desc: 'General event — no registration' },
                  ].map(opt => (
                    <label key={opt.value} className={`flex flex-col p-3 border-2 rounded-lg cursor-pointer transition-colors ${formData.event_type === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="event_type" value={opt.value} checked={formData.event_type === opt.value}
                        onChange={() => setFormData({ ...formData, event_type: opt.value })} className="sr-only" />
                      <span className="font-medium text-gray-900">{opt.label}</span>
                      <span className="text-xs text-gray-500 mt-1">{opt.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Event Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={formData.event_type === 'wrap_around_care' ? 'e.g., Wrap Around Care - Spring Term' : formData.event_type === 'others' ? 'e.g., School Open Day' : 'e.g., Parents Evening - Term 1 2025'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📅 {formData.event_type === 'wrap_around_care' ? 'Term *' : 'Event Date *'}
                </label>
                {formData.event_type === 'wrap_around_care' ? (
                  <select
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">-- Select Term --</option>
                    {TERM_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : (
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                )}
              </div>

              {/* Meeting type - only for Parents Evening */}
              {formData.event_type === 'parents_evening' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    💻 Meeting Type
                  </label>
                  <select
                    value={formData.meeting_type}
                    onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="online">🖥️ Online (Microsoft Teams)</option>
                    <option value="in-person">🏫 In-Person</option>
                  </select>
                </div>
              )}

              {/* WAC cost fields */}
              {formData.event_type === 'wrap_around_care' && (
                <>
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-1">🌄 Morning Session</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                        <input type="time" value={formData.morning_start}
                          onChange={(e) => setFormData({ ...formData, morning_start: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                        <input type="time" value={formData.morning_end}
                          onChange={(e) => setFormData({ ...formData, morning_end: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cost (QAR)</label>
                        <input type="number" step="0.01" min="0" value={formData.morning_cost}
                          onChange={(e) => setFormData({ ...formData, morning_cost: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 50.00" />
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-1">🌇 Afternoon Session</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                        <input type="time" value={formData.afternoon_start}
                          onChange={(e) => setFormData({ ...formData, afternoon_start: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                        <input type="time" value={formData.afternoon_end}
                          onChange={(e) => setFormData({ ...formData, afternoon_end: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cost (QAR)</label>
                        <input type="number" step="0.01" min="0" value={formData.afternoon_cost}
                          onChange={(e) => setFormData({ ...formData, afternoon_cost: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 50.00" />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📚 Assign to Year Groups (Leave empty for all years)
                </label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: 'Pre School', label: 'Pre School' },
                    { value: 'Reception', label: 'Reception' },
                    ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(y => ({ value: y.toString(), label: `Year ${y}` }))
                  ].map(({ value, label }) => (
                    <label key={value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.years.includes(value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              years: [...formData.years, value]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              years: formData.years.filter(y => y !== value)
                            });
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 whitespace-nowrap">{label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Leave unchecked to make this event visible to parents of all year groups
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🕐 Start Time *
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🕐 End Time *
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {formData.event_type === 'parents_evening' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ⏱️ Appointment Duration
                  </label>
                  <select
                    value={formData.slot_duration}
                    onChange={(e) => setFormData({ ...formData, slot_duration: parseInt(e.target.value) })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes (Recommended)</option>
                    <option value={15}>15 minutes</option>
                    <option value={20}>20 minutes</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <span>✅</span>
                    <span>{formData.event_type === 'parents_evening' ? 'Create Event & Generate Slots' : 'Create Event'}</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Event Form */}
      {editingEvent && (
        <div className="mb-8 bg-white rounded-xl p-6 border">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">✏️ Edit Event</h3>
            <button
              onClick={() => setEditingEvent(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            updateEvent(editingEvent);
          }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Event Type */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🗂️ Event Type
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { value: 'parents_evening', label: '🎓 Parents Evening', desc: 'Teacher appointment slots' },
                    { value: 'wrap_around_care', label: '🌅 Wrap Around Care', desc: 'Morning & afternoon sessions with cost' },
                    { value: 'others', label: '📌 Others', desc: 'General event — no registration' },
                  ].map(opt => (
                    <label key={opt.value} className={`flex flex-col p-3 border-2 rounded-lg cursor-pointer transition-colors ${(editingEvent.event_type || 'parents_evening') === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="edit_event_type" value={opt.value}
                        checked={(editingEvent.event_type || 'parents_evening') === opt.value}
                        onChange={() => setEditingEvent({ ...editingEvent, event_type: opt.value })} className="sr-only" />
                      <span className="font-medium text-gray-900">{opt.label}</span>
                      <span className="text-xs text-gray-500 mt-1">{opt.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Event Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Name *
                </label>
                <input
                  type="text"
                  value={editingEvent.name}
                  onChange={(e) => setEditingEvent({ ...editingEvent, name: e.target.value })}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📅 {(editingEvent.event_type || 'parents_evening') === 'wrap_around_care' ? 'Term *' : 'Event Date *'}
                </label>
                {(editingEvent.event_type || 'parents_evening') === 'wrap_around_care' ? (
                  <select
                    value={editingEvent.date}
                    onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">-- Select Term --</option>
                    {TERM_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : (
                  <input
                    type="date"
                    value={editingEvent.date}
                    onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                )}
              </div>

              {(editingEvent.event_type || 'parents_evening') === 'parents_evening' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    💻 Meeting Type
                  </label>
                  <select
                    value={editingEvent.meeting_type}
                    onChange={(e) => setEditingEvent({ ...editingEvent, meeting_type: e.target.value })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="online">🖥️ Online (Microsoft Teams)</option>
                    <option value="in-person">🏫 In-Person</option>
                  </select>
                </div>
              )}

              {(editingEvent.event_type || 'parents_evening') === 'wrap_around_care' && (
                <>
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-1">🌄 Morning Session</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                        <input type="time" value={editingEvent.morning_start || ''}
                          onChange={(e) => setEditingEvent({ ...editingEvent, morning_start: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                        <input type="time" value={editingEvent.morning_end || ''}
                          onChange={(e) => setEditingEvent({ ...editingEvent, morning_end: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cost (QAR)</label>
                        <input type="number" step="0.01" min="0" value={editingEvent.morning_cost || ''}
                          onChange={(e) => setEditingEvent({ ...editingEvent, morning_cost: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 50.00" />
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-1">🌇 Afternoon Session</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                        <input type="time" value={editingEvent.afternoon_start || ''}
                          onChange={(e) => setEditingEvent({ ...editingEvent, afternoon_start: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                        <input type="time" value={editingEvent.afternoon_end || ''}
                          onChange={(e) => setEditingEvent({ ...editingEvent, afternoon_end: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cost (QAR)</label>
                        <input type="number" step="0.01" min="0" value={editingEvent.afternoon_cost || ''}
                          onChange={(e) => setEditingEvent({ ...editingEvent, afternoon_cost: e.target.value })}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 50.00" />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🕐 Start Time *
                </label>
                <input
                  type="time"
                  value={editingEvent.start_time}
                  onChange={(e) => setEditingEvent({ ...editingEvent, start_time: e.target.value })}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🕐 End Time *
                </label>
                <input
                  type="time"
                  value={editingEvent.end_time}
                  onChange={(e) => setEditingEvent({ ...editingEvent, end_time: e.target.value })}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {(editingEvent.event_type || 'parents_evening') === 'parents_evening' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ⏱️ Slot Duration (minutes) *
                  </label>
                  <select
                    value={editingEvent.slot_duration}
                    onChange={(e) => setEditingEvent({ ...editingEvent, slot_duration: parseInt(e.target.value) })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={20}>20 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <span>💾</span>
                    <span>Update Event</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setEditingEvent(null)}
                className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Events */}
      <div>
        <h3 className="text-lg font-medium mb-4 text-gray-900">📋 Existing Events</h3>
        {events.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <div className="text-gray-400 text-6xl mb-4">📅</div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Events Created Yet</h4>
            <p className="text-gray-600 mb-4">Create your first parents evening event to get started.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Create First Event
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {events.map((event) => (
              <div key={event.id} className="bg-white border rounded-xl p-6 shadow-sm">
                <div className="flex justify-between items-start flex-col md:flex-row gap-4">
                  <div className="flex-1 w-full">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{event.name}</h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        (event.event_type || 'parents_evening') === 'wrap_around_care'
                          ? 'bg-orange-100 text-orange-800'
                          : (event.event_type || 'parents_evening') === 'others'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {(event.event_type || 'parents_evening') === 'wrap_around_care' ? '🌅 Wrap Around Care'
                          : (event.event_type || 'parents_evening') === 'others' ? '📌 Other Event'
                          : '🎓 Parents Evening'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <span>📅</span>
                        <span>{new Date(event.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>🕐</span>
                        <span>{event.start_time} - {event.end_time}</span>
                      </div>
                      {(event.event_type || 'parents_evening') === 'parents_evening' ? (
                        <>
                          <div className="flex items-center space-x-2">
                            <span>⏱️</span>
                            <span>{event.slot_duration} min slots</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span>💻</span>
                            <span>{event.meeting_type === 'online' ? 'Online' : 'In-Person'}</span>
                          </div>
                        </>
                      ) : (event.event_type || 'parents_evening') === 'wrap_around_care' ? (
                        <>
                          <div className="flex items-center space-x-2">
                            <span>🌄</span>
                            <span>Morning: QAR {event.morning_cost || '0.00'}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span>🌇</span>
                            <span>Afternoon: QAR {event.afternoon_cost || '0.00'}</span>
                          </div>
                        </>
                      ) : null}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                      {event.years && event.years.split(',').length > 0 ? (
                        <>
                          <span className="text-xs text-gray-600 font-medium">Year Groups:</span>
                          <div className="flex flex-wrap gap-1">
                            {event.years.split(',').map(year => (
                              <span key={year} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Year {year}
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-gray-600 font-medium">All Year Groups</span>
                      )}
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        (event.active !== false && event.status !== 'closed') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {(event.active !== false && event.status !== 'closed') ? '✅ Active' : '🔒 Closed'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 w-full md:w-auto justify-start md:justify-end">
                    <button
                      onClick={() => setEditingEvent(event)}
                      className="text-blue-600 hover:text-blue-800 px-3 py-1 text-sm border border-blue-200 rounded-lg hover:bg-blue-50 whitespace-nowrap"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => togglePublishStatus(event)}
                      className={`px-3 py-1 text-sm border rounded-lg font-medium transition-colors whitespace-nowrap ${
                        event.published 
                          ? 'text-red-600 hover:text-red-800 border-red-300 bg-red-50 hover:bg-red-100' 
                          : 'text-gray-600 hover:text-gray-800 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {event.published ? '🔴 Stop Sharing' : '⚪ Publish to Parents'}
                    </button>
                    <button
                      onClick={() => toggleEventStatus(event)}
                      className={`px-3 py-1 text-sm border rounded-lg whitespace-nowrap ${
                        (event.active !== false && event.status !== 'closed')
                          ? 'text-orange-600 hover:text-orange-800 border-orange-200 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-800 border-green-200 hover:bg-green-50'
                      }`}
                    >
                      {(event.active !== false && event.status !== 'closed') ? '🔒 Close' : '✅ Activate'}
                    </button>
                    <button
                      onClick={() => viewEventBookings(event)}
                      className="text-purple-600 hover:text-purple-800 px-3 py-1 text-sm border border-purple-200 rounded-lg hover:bg-purple-50 whitespace-nowrap"
                    >
                      {(event.event_type || 'parents_evening') === 'parents_evening' ? '📊 Bookings' : '📋 Registrations'}
                    </button>
                    <button
                      onClick={() => deleteEvent(event.id)}
                      className="text-red-600 hover:text-red-800 px-3 py-1 text-sm border border-red-200 rounded-lg hover:bg-red-50 whitespace-nowrap"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System Information */}
      <div className="mt-8 bg-blue-50 rounded-xl p-6">
        <h4 className="font-medium text-blue-900 mb-3">📋 System Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <span>✅</span>
              <span>Events automatically generate time slots for all active teachers</span>
            </li>
            <li className="flex items-center space-x-2">
              <span>✅</span>
              <span>Parents can book one appointment per teacher per child</span>
            </li>
          </ul>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <span>✅</span>
              <span>Meeting links are generated automatically for online events</span>
            </li>
            <li className="flex items-center space-x-2">
              <span>✅</span>
              <span>Bookings close 24 hours before the event</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Print styles for PDF export
const printStyles = `
  @media print {
    /* Hide non-printable elements */
    button, 
    nav,
    .no-print {
      display: none !important;
    }

    /* Optimize page layout for printing */
    body {
      font-size: 12pt;
      line-height: 1.5;
      color: #000;
      background: white;
    }

    .p-6 {
      padding: 0 !important;
    }

    /* Schedule cards styling for print */
    div[class*="bg-blue-50"],
    div[class*="bg-red-50"],
    div[class*="bg-gray-50"] {
      border: 1px solid #333 !important;
      page-break-inside: avoid;
      margin-bottom: 12pt;
      padding: 8pt !important;
    }

    /* Title styling */
    h2 {
      font-size: 16pt;
      margin-bottom: 12pt;
      margin-top: 0;
    }

    /* Remove shadows and unnecessary styling */
    * {
      box-shadow: none !important;
      page-break-inside: avoid;
    }

    /* Ensure content is black for printing */
    .text-blue-800,
    .text-gray-700,
    .text-gray-600 {
      color: #000 !important;
    }

    /* Badges styling for print */
    span[class*="bg-blue-100"],
    span[class*="bg-red-100"],
    span[class*="bg-gray-100"] {
      border: 1px solid #333;
      padding: 2pt 4pt;
    }

    /* Links styling for print */
    a {
      color: #000;
      text-decoration: underline;
    }
  }
`;

// Inject print styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = printStyles;
  document.head.appendChild(styleSheet);
}

export default ParentsEveningSystem;
